import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Select from 'ravenjs/lib/Select';

import { COLOR_TOKENS } from 'constants/quartz/color_tokens';
import { PRIMITIVE_COLORS } from 'constants/quartz/primitive_colors';

const ROBOTO = "'Roboto', Helvetica, Arial, sans-serif";

/* Filled caret-down icon matching the Figma spec (7.17px x 3.92px). */
const CaretDownFilled = () => (
    <svg
        width="7"
        height="4"
        viewBox="0 0 7 4"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path d="M3.5 4L0 0H7L3.5 4Z" fill="currentColor" />
    </svg>
);

/* Custom dropdown indicator using the filled caret-down icon. */
const DropdownIndicator = ({ innerProps }) => {
    return (
        <div
            {...innerProps}
            style={{
                padding: '0 0 0 4px',
                display: 'flex',
                alignItems: 'center',
                color: PRIMITIVE_COLORS.neutral.black,
            }}
        >
            <CaretDownFilled />
        </div>
    );
};

DropdownIndicator.propTypes = {
    innerProps: PropTypes.object,
};

DropdownIndicator.defaultProps = {
    innerProps: {},
};

const RowsPerPageWrapper = styled.div`
    align-items: center;
    display: flex;
    flex-direction: row;
    gap: 8px;
    white-space: nowrap;
`;

const RowsPerPageLabel = styled.span`
    color: ${COLOR_TOKENS.text.secondary};
    font-family: ${ROBOTO};
    font-size: 14px;
    font-weight: 400;
    line-height: 150%;
`;

/**
 * Quartz-scoped `react-select` style overrides for the pagination
 * rows-per-page selector. Scoped to this component only so the shared
 * `ravenjs/lib/Select` behaves identically everywhere else.
 */
const SELECT_STYLES = {
    container: {
        display: 'inline-flex',
    },
    control: {
        minHeight: 'auto',
        height: 'auto',
        border: 'none',
        borderRadius: 0,
        backgroundColor: 'transparent',
        boxShadow: 'none',
        padding: 0,
        color: COLOR_TOKENS.text.primary,
        cursor: 'pointer',
        '&:hover': {
            borderColor: 'transparent',
        },
    },
    valueContainer: {
        padding: 0,
    },
    singleValue: {
        margin: 0,
        color: COLOR_TOKENS.text.primary,
        fontFamily: ROBOTO,
        fontSize: '14px',
        fontWeight: 400,
    },
    dropdownIndicator: {
        padding: '0 0 0 4px',
        color: PRIMITIVE_COLORS.neutral.black,
        '& svg': {
            width: '7px',
            height: '4px',
        },
    },
    menu: {
        minWidth: '72px',
        border: `1px solid ${PRIMITIVE_COLORS.neutral.graphite[300]}`,
        borderRadius: '4px',
        marginTop: '4px',
    },
    menuList: {
        padding: '4px 0',
    },
    option: {
        padding: '6px 12px',
        fontSize: '14px',
    },
    menuPortal: {
        zIndex: 9999,
    },
};

function PaginationBarRowsPerPage({
    label,
    onChange,
    rowsPerPage,
    rowsPerPageOptions,
    menuPlacement,
}) {
    const options = rowsPerPageOptions.map(option => ({ label: option, value: option }));
    const value = { label: rowsPerPage, value: rowsPerPage };
    // Portal the menu to `document.body` so it is not clipped by the redesign
    // table wrapper's `overflow: hidden` (the attached-footer container).
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : undefined;

    return (
        <RowsPerPageWrapper>
            {label && <RowsPerPageLabel>{label}</RowsPerPageLabel>}
            <Select
                components={{ DropdownIndicator }}
                defaultValue={value}
                isClearable={false}
                isSearchable={false}
                menuPlacement={menuPlacement}
                menuPortalTarget={menuPortalTarget}
                menuPosition="fixed"
                name="pagination-bar-rows-per-page"
                onChange={onChange}
                options={options}
                styles={SELECT_STYLES}
                value={value}
            />
        </RowsPerPageWrapper>
    );
}

PaginationBarRowsPerPage.propTypes = {
    label: PropTypes.node,
    menuPlacement: PropTypes.string,
    onChange: PropTypes.func,
    rowsPerPage: PropTypes.number,
    rowsPerPageOptions: PropTypes.array,
};

PaginationBarRowsPerPage.defaultProps = {
    label: 'Rows per page:',
    menuPlacement: 'auto',
    onChange: () => {},
    rowsPerPage: 10,
    rowsPerPageOptions: [5, 10, 15, 20],
};

export default PaginationBarRowsPerPage;
